import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Copy, FileText, MessageSquare, Share2 } from "lucide-react";

interface Deal {
  id: string;
  deal_title: string;
  property_address: string | null;
  buyer_name: string;
  date_of_sale: string | null;
  sales_price: number | null;
  down_payment_amount: number | null;
  first_payment_date: string | null;
  payment_amount: number | null;
  interest_rate: number | null;
  current_balance: number | null;
  payments_remaining: number | null;
}

export function DealQuoteSheet() {
  const { toast } = useToast();
  const [deals, setDeals] = useState<Deal[]>([]);
  const [selectedDealId, setSelectedDealId] = useState<string>("");
  const [loading, setLoading] = useState(true);

  // Quote fields
  const [propertyAddress, setPropertyAddress] = useState("");
  const [buyerName, setBuyerName] = useState("");
  const [saleDate, setSaleDate] = useState("");
  const [salePrice, setSalePrice] = useState<number | "">("");
  const [downPayment, setDownPayment] = useState<number | "">("");
  const [firstPaymentDate, setFirstPaymentDate] = useState("");
  const [paymentAmount, setPaymentAmount] = useState<number | "">("");
  const [interestRate, setInterestRate] = useState<number | "">("");
  const [currentBalance, setCurrentBalance] = useState<number | "">("");
  const [offerAmount, setOfferAmount] = useState<number | "">("");
  const [paymentsMade, setPaymentsMade] = useState<number | "">(0);
  const [recipientPhone, setRecipientPhone] = useState("");

  useEffect(() => {
    fetchDeals();
  }, []);

  const fetchDeals = async () => {
    const { data, error } = await supabase
      .from("deals")
      .select("id, deal_title, property_address, buyer_name, date_of_sale, sales_price, down_payment_amount, first_payment_date, payment_amount, interest_rate, current_balance, payments_remaining")
      .order("created_at", { ascending: false });

    if (error) {
      console.error("Error fetching deals:", error);
    } else {
      setDeals(data || []);
    }
    setLoading(false);
  };

  const handleDealSelect = (dealId: string) => {
    setSelectedDealId(dealId);
    const deal = deals.find(d => d.id === dealId);
    if (deal) {
      setPropertyAddress(deal.property_address || "");
      setBuyerName(deal.buyer_name || "");
      setSaleDate(deal.date_of_sale || "");
      setSalePrice(deal.sales_price || "");
      setDownPayment(deal.down_payment_amount || "");
      setFirstPaymentDate(deal.first_payment_date || "");
      setPaymentAmount(deal.payment_amount || "");
      setInterestRate(deal.interest_rate || "");
      setCurrentBalance(deal.current_balance || "");
      setPaymentsMade(0);
      setOfferAmount("");
    }
  };

  const formatCurrency = (value: number | "") => {
    if (value === "" || value === null || value === undefined) return "";
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
    }).format(value);
  };

  const formatDate = (dateStr: string) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-US", { month: "2-digit", day: "2-digit", year: "numeric" });
  };

  // Calculations
  const cashFromDownPayment = typeof downPayment === "number" ? downPayment : 0;
  const paymentsReceived = (typeof paymentsMade === "number" ? paymentsMade : 0) * (typeof paymentAmount === "number" ? paymentAmount : 0);
  const offer = typeof offerAmount === "number" ? offerAmount : 0;
  const totalCashReceived = cashFromDownPayment + paymentsReceived + offer;

  const generateQuoteText = () => {
    const lines = [
      `QUOTE SUMMARY`,
      `─────────────────────────────────`,
      `Property Address: ${propertyAddress}`,
      `Buyer Name: ${buyerName}`,
      `Sale Date: ${formatDate(saleDate)}`,
      `Sale Price: ${formatCurrency(salePrice)}`,
      `Down Payment Amount: ${formatCurrency(downPayment)}`,
      `First Payment Date: ${formatDate(firstPaymentDate)}`,
      `Payment Amount: ${formatCurrency(paymentAmount)}`,
      `Interest Rate: ${interestRate}%`,
      `Current Balance: ${formatCurrency(currentBalance)}`,
      ``,
      `─────────────────────────────────`,
      `Cash Received from Down Payment: ${formatCurrency(cashFromDownPayment)}`,
      `Payments Received: ${paymentsMade} x ${formatCurrency(paymentAmount)} = ${formatCurrency(paymentsReceived)}`,
      `Offer from Contract Funding Group: ${formatCurrency(offer)}`,
      ``,
      `TOTAL CASH RECEIVED: ${formatCurrency(totalCashReceived)}`,
      `─────────────────────────────────`,
    ];
    return lines.join("\n");
  };

  // Compact SMS-friendly format
  const generateSMSText = () => {
    const lines = [
      `CONTRACT QUOTE`,
      ``,
      `${propertyAddress}`,
      `Buyer: ${buyerName}`,
      ``,
      `Sale: ${formatCurrency(salePrice)}`,
      `Down: ${formatCurrency(downPayment)}`,
      `Pmt: ${formatCurrency(paymentAmount)}/mo @ ${interestRate}%`,
      `Balance: ${formatCurrency(currentBalance)}`,
      ``,
      `YOUR CASH:`,
      `Down: ${formatCurrency(cashFromDownPayment)}`,
      `${paymentsMade} pmts: ${formatCurrency(paymentsReceived)}`,
      `Our Offer: ${formatCurrency(offer)}`,
      ``,
      `TOTAL: ${formatCurrency(totalCashReceived)}`,
    ];
    return lines.join("\n");
  };

  const handleCopyQuote = async () => {
    const quoteText = generateQuoteText();
    try {
      await navigator.clipboard.writeText(quoteText);
      toast({
        title: "Copied!",
        description: "Quote copied to clipboard",
      });
    } catch (err) {
      toast({
        title: "Error",
        description: "Failed to copy quote",
        variant: "destructive",
      });
    }
  };

  const handleShareQuote = async () => {
    const smsText = generateSMSText();
    
    // Try Web Share API first (works great on mobile)
    if (navigator.share) {
      try {
        await navigator.share({
          title: "Contract Quote",
          text: smsText,
        });
        return;
      } catch (err) {
        // User cancelled or share failed, fall through to SMS link
      }
    }
    
    // Fallback: Open SMS with pre-filled message
    const smsUrl = `sms:${recipientPhone}?body=${encodeURIComponent(smsText)}`;
    window.open(smsUrl, "_blank");
  };

  const handleCopySMS = async () => {
    const smsText = generateSMSText();
    try {
      await navigator.clipboard.writeText(smsText);
      toast({
        title: "Copied!",
        description: "SMS-ready quote copied to clipboard",
      });
    } catch (err) {
      toast({
        title: "Error",
        description: "Failed to copy quote",
        variant: "destructive",
      });
    }
  };

  const clearForm = () => {
    setSelectedDealId("");
    setPropertyAddress("");
    setBuyerName("");
    setSaleDate("");
    setSalePrice("");
    setDownPayment("");
    setFirstPaymentDate("");
    setPaymentAmount("");
    setInterestRate("");
    setCurrentBalance("");
    setOfferAmount("");
    setPaymentsMade(0);
  };

  return (
    <div className="max-w-2xl mx-auto p-4 space-y-4">
      <Card>
        <CardHeader className="pb-4">
          <CardTitle className="flex items-center gap-2 text-xl">
            <FileText className="h-5 w-5" />
            Deal Quote Sheet
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Deal Selector */}
          <div className="space-y-2">
            <Label>Load from Deal</Label>
            <Select value={selectedDealId} onValueChange={handleDealSelect}>
              <SelectTrigger>
                <SelectValue placeholder="Select a deal to load..." />
              </SelectTrigger>
              <SelectContent>
                {deals.map((deal) => (
                  <SelectItem key={deal.id} value={deal.id}>
                    {deal.deal_title} - {deal.buyer_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Form Fields */}
          <div className="grid gap-3">
            <div className="space-y-1">
              <Label className="text-sm text-muted-foreground">Property Address</Label>
              <Input 
                value={propertyAddress} 
                onChange={(e) => setPropertyAddress(e.target.value)}
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-sm text-muted-foreground">Buyer Name</Label>
                <Input 
                  value={buyerName} 
                  onChange={(e) => setBuyerName(e.target.value)}
                />
              </div>
              <div className="space-y-1">
                <Label className="text-sm text-muted-foreground">Sale Date</Label>
                <Input 
                  type="date"
                  value={saleDate} 
                  onChange={(e) => setSaleDate(e.target.value)}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-sm text-muted-foreground">Sale Price</Label>
                <Input 
                  type="number"
                  value={salePrice} 
                  onChange={(e) => setSalePrice(e.target.value ? parseFloat(e.target.value) : "")}
                />
              </div>
              <div className="space-y-1">
                <Label className="text-sm text-muted-foreground">Down Payment</Label>
                <Input 
                  type="number"
                  value={downPayment} 
                  onChange={(e) => setDownPayment(e.target.value ? parseFloat(e.target.value) : "")}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-sm text-muted-foreground">First Payment Date</Label>
                <Input 
                  type="date"
                  value={firstPaymentDate} 
                  onChange={(e) => setFirstPaymentDate(e.target.value)}
                />
              </div>
              <div className="space-y-1">
                <Label className="text-sm text-muted-foreground">Payment Amount</Label>
                <Input 
                  type="number"
                  value={paymentAmount} 
                  onChange={(e) => setPaymentAmount(e.target.value ? parseFloat(e.target.value) : "")}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-sm text-muted-foreground">Interest Rate (%)</Label>
                <Input 
                  type="number"
                  step="0.01"
                  value={interestRate} 
                  onChange={(e) => setInterestRate(e.target.value ? parseFloat(e.target.value) : "")}
                />
              </div>
              <div className="space-y-1">
                <Label className="text-sm text-muted-foreground">Payments Made</Label>
                <Input 
                  type="number"
                  value={paymentsMade} 
                  onChange={(e) => setPaymentsMade(e.target.value ? parseInt(e.target.value) : "")}
                />
              </div>
            </div>

            <div className="space-y-1">
              <Label className="text-sm text-muted-foreground">Current Balance</Label>
              <Input 
                type="number"
                value={currentBalance} 
                onChange={(e) => setCurrentBalance(e.target.value ? parseFloat(e.target.value) : "")}
              />
            </div>
          </div>

          {/* Separator */}
          <div className="border-t pt-4">
            <div className="space-y-1">
              <Label className="text-sm font-medium">Offer from Contract Funding Group</Label>
              <Input 
                type="number"
                value={offerAmount} 
                onChange={(e) => setOfferAmount(e.target.value ? parseFloat(e.target.value) : "")}
                className="text-lg font-medium"
              />
            </div>
          </div>

          {/* Summary */}
          <div className="bg-muted/50 rounded-lg p-4 space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Cash from Down Payment:</span>
              <span>{formatCurrency(cashFromDownPayment)}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Payments Received ({paymentsMade || 0} x {formatCurrency(paymentAmount)}):</span>
              <span>{formatCurrency(paymentsReceived)}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Offer Amount:</span>
              <span>{formatCurrency(offer)}</span>
            </div>
            <div className="border-t pt-2 flex justify-between font-semibold">
              <span>Total Cash Received:</span>
              <span className="text-primary">{formatCurrency(totalCashReceived)}</span>
            </div>
          </div>

          {/* Send as Text */}
          <div className="border-t pt-4 space-y-3">
            <Label className="text-sm font-medium">Send as Text Message</Label>
            <p className="text-xs text-muted-foreground">
              Enter the seller's phone number and tap "Open Texting App" to compose a text with the quote pre-filled, or copy the quote and paste it manually.
            </p>
            <div className="flex gap-2">
              <Input 
                placeholder="Enter phone number"
                value={recipientPhone}
                onChange={(e) => setRecipientPhone(e.target.value)}
                className="flex-1"
                type="tel"
              />
              <Button onClick={handleShareQuote} variant="default">
                <MessageSquare className="h-4 w-4 mr-2" />
                Open Texting App
              </Button>
            </div>
            <Button onClick={handleCopySMS} variant="outline" className="w-full">
              <Copy className="h-4 w-4 mr-2" />
              Copy Quote to Clipboard
            </Button>
            <p className="text-xs text-muted-foreground text-center">
              After copying, open your Messages app and paste (Ctrl+V or long-press → Paste)
            </p>
          </div>

          {/* Actions */}
          <div className="flex gap-2">
            <Button onClick={handleCopyQuote} variant="outline" className="flex-1">
              <Copy className="h-4 w-4 mr-2" />
              Copy Full Quote
            </Button>
            <Button variant="ghost" onClick={clearForm}>
              Clear
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
